import React, { useState, useEffect } from 'react';
import { X, User, FileText, Award, Briefcase } from 'lucide-react';
import api from '../../services/api';

const ProfileShareModal = ({ connection, isOpen, onClose }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && connection?.id) {
      loadSharedProfile();
    }
  }, [isOpen, connection?.id]);

  const loadSharedProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await api.get(`/connection-chat/shared-profile/${connection.id}`);
      const data = res?.data || res;

      if (data.success) {
        if (data.shared) {
          setProfile(data.profile);
        } else {
          setError(data.message || 'Profile not shared yet');
        }
      }
    } catch (err) {
      setError('Failed to load profile');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 50, padding: '16px'
    }}>
      <div style={{
        background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '500px',
        maxHeight: '80vh', overflow: 'hidden',
        boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #a855f7, #ec4899)',
          padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#fff' }}>
            <User size={24} />
            <div>
              <h3 style={{ fontWeight: 600, margin: 0 }}>Shared Profile</h3>
              <p style={{ fontSize: '13px', opacity: 0.8, margin: 0 }}>{profile?.name || 'Loading...'}</p>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)',
            cursor: 'pointer'
          }}>
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px', overflowY: 'auto', maxHeight: '60vh' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
              <div style={{
                width: '32px', height: '32px', border: '4px solid #a855f7',
                borderTopColor: 'transparent', borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <p style={{ color: '#6b7280' }}>{error}</p>
              <p style={{ fontSize: '13px', color: '#9ca3af', marginTop: '8px' }}>
                Ask them to share their profile through SkillBot chat
              </p>
            </div>
          ) : profile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Basic Info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                  width: '64px', height: '64px',
                  background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                  borderRadius: '50%', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', color: '#fff', fontSize: '24px', fontWeight: 700
                }}>
                  {profile.name?.charAt(0) || 'U'}
                </div>
                <div>
                  <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#111827', margin: 0 }}>{profile.name}</h2>
                  <p style={{ color: '#6b7280', textTransform: 'capitalize', margin: '4px 0 0' }}>
                    {profile.entityType} &middot; {profile.vertical}
                  </p>
                </div>
              </div>

              {/* Skills */}
              {profile.skills?.length > 0 && (
                <div>
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                    <Award size={16} /> Skills
                  </h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {profile.skills.map((skill, i) => (
                      <span key={i} style={{
                        padding: '4px 12px', background: '#f3e8ff',
                        color: '#7c3aed', borderRadius: '20px', fontSize: '13px'
                      }}>
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Professional Profile */}
              {profile.professionalProfile && (
                <div>
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                    <Briefcase size={16} /> Experience
                  </h3>
                  <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '8px' }}>
                    {profile.professionalProfile.summary && (
                      <p style={{ color: '#374151', fontSize: '14px', margin: 0 }}>
                        {profile.professionalProfile.summary}
                      </p>
                    )}
                    {profile.professionalProfile.experience && (
                      <p style={{ color: '#6b7280', fontSize: '13px', marginTop: '8px' }}>
                        {profile.professionalProfile.experience} years experience
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Resume Link */}
              {profile.resumeUrl && (
                <div>
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                    <FileText size={16} /> Resume
                  </h3>
                  <a
                    href={profile.resumeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '8px',
                      padding: '8px 16px', background: '#a855f7', color: '#fff',
                      borderRadius: '8px', textDecoration: 'none', fontSize: '14px'
                    }}
                  >
                    <FileText size={16} /> View Resume
                  </a>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ProfileShareModal;
